/**
 * Web-side client for the Postgres-backed Library Filter endpoint
 * (`GET /api/v2/library`). Replaces the Firestore-direct filtering path
 * (`libraryFirestoreFilters.ts`, retired in Phase 4).
 *
 * Modeled on `devices.ts` (NOT `uploadApi.ts`, which is a Cloud Functions
 * client): a pure, Jest-testable kernel that reuses the caller's existing
 * sign-in — the Vue layer supplies `idToken` (`getIdToken()`), `deviceId`
 * (`X-Device-Id`), and `baseUrl` (`VUE_APP_API_BASE_URL`). The account rides
 * the request itself, as `team_id` on team reads.
 *
 * The gallery needs the run shape: run objects + a side `tools` map,
 * from which it mints its own Storage URLs (exactly as the Firestore gallery
 * does) — the server does NOT sign URLs on this path. There is no `shape`
 * parameter; the API serves that shape because this browser is registered as
 * the first-party web client (`web_app`). Losing the device registration means
 * silently getting the public `result` shape instead, which the gallery cannot
 * render, so `deviceId` is effectively required here even though the HTTP call
 * succeeds without it.
 */

import { forgetWebClientDeviceIfRejected } from './webClientDevice';

export type LibraryScope = 'personal' | 'team';

/**
 * The applied facet model the filter panel produces (aspect / resolution / tool /
 * tags / date). Defined here as the single source for the REST client.
 */
export interface LibraryFacetFilters {
	aspectRatioFilter?: string | null;
	resolutionFilter?: string | null;
	toolFilter?: string | null;
	modelFamilyTagFilter?: string | null;
	mediaTagFilter?: string | null;
	dateFromFilter?: string | null;
	dateToFilter?: string | null;
	// Favorite overlay: 'favorited' narrows to results the caller has favorited.
	// Distinct from the `favorites` flag (the dedicated Favorites tab).
	favoriteFilter?: string | null;
	// Library Search term. Ranks the matches best first in one page.
	searchFilter?: string | null;
}

// The server refuses a shorter `search`, so a shorter term is no search at all.
export const LIBRARY_SEARCH_MIN_LENGTH = 2;

export interface LibraryRequest {
	scope?: 'mine' | 'all';
	filters?: LibraryFacetFilters;
	userId?: string | null;      // filter-by-user (scope=all only)
	favorites?: boolean;         // caller's own favorited results only
	teamIds?: string[] | null;   // the teams to read (server verifies each)
	nodeId?: string | null;      // favorites per-node scope (board context)
	runnitId?: string | null;    // favorites per-node scope (board context)
	cursor?: string | null;
	limit?: number;
}

// --- response shapes (node_run) --------------------------------------------

export interface LibraryNodeRunResultFile {
	name?: string;
	width?: number;
	height?: number;
	contentType?: string;
	aspectRatio?: number;
	duration?: number;
	size?: number;
	[key: string]: unknown;
}

export interface LibraryNodeRunResult {
	uuid: string;
	type?: string;
	file?: LibraryNodeRunResultFile;
	displayFile?: LibraryNodeRunResultFile;
	text?: string | null;
	templateOriginalData?: Record<string, unknown> | null;
	[key: string]: unknown;
}

export interface LibraryNodeRun {
	id: string;
	tool_id: string | null;
	user_id: string | null;
	team_id: string | null;
	// Synced columns the partial needs: email for the ImageInfo attribution,
	// board_id/node_id because the resultFavorites rule compares them against
	// the source run doc.
	email?: string | null;
	board_id?: string | null;
	node_id?: string | null;
	init_at: string | null;
	done_at: string | null;
	results: LibraryNodeRunResult[];
}

export interface LibraryNodeDef {
	id: string;
	title: string | null;
	tags: Record<string, unknown> | null;
	appType?: string | null;
}

export interface LibraryPage {
	data: LibraryNodeRun[];
	nodeDefs: Record<string, LibraryNodeDef>;
	nextCursor: string | null;
	hasMore: boolean;
}

export interface LibraryFetchInput {
	baseUrl: string;
	idToken: string;
	/**
	 * This browser's Connected Device id, from registering as the first-party
	 * web client. It is what makes the API serve the `node_run` shape the gallery
	 * renders; omit it and the request still succeeds but returns the public
	 * `result` shape, which has no `tools` map.
	 */
	deviceId?: string | null;
	request: LibraryRequest;
}

export interface LibraryTeamOption {
	value: string;
	text: string;
	disabled: boolean;
}

interface LibraryTeamOptionsInput {
	baseUrl: string;
	idToken: string;
}

export type LibraryFetchResult =
	| { ok: true; page: LibraryPage }
	| {
		ok: false;
		reason: 'network' | 'unauthorized' | 'forbidden' | 'failed';
		// The server's structured error envelope ({ error: { code, message } }).
		// Previously discarded on 401/403, which meant a "not a member of this
		// team", "verify your email", and "missing MANAGE_ALL_GENERATIONS" denial
		// were indistinguishable to the caller and to whoever was debugging.
		code?: string;
		message?: string;
	};

/**
 * Resolve the account selection for a library request. Pure core of
 * ImageGallery's resolveLibraryScope, extracted so request construction is
 * testable without mounting Vue (plan 2026-07-22, Task 3).
 *
 * The panel's applied scope wins when it is team; otherwise the host nav tab
 * decides (TEAM -> team, anything else -> personal). On the wire the account IS
 * the `team_id` parameter — named means the team, absent means personal — and a
 * team read is `scope=all` (the whole team's rows, MANAGE_ALL-gated
 * server-side).
 *
 * Plan Path A: a team view with NO active team id has nothing to name in
 * `team_id`, and sending nothing would quietly read the personal account —
 * the exact swap the old header scheme 403'd on. Diagnosis during PR #625
 * proved the state reachable, so the caller gets `blocked: true` and skips the
 * request entirely instead of firing one that answers the wrong question.
 */
export function resolveLibraryScopeAndAccount (input: {
	panelScope?: LibraryScope | '' | null;
	selectedFilter?: string | null;
	activeTeamId?: string | null;
}): { scope: 'mine' | 'all'; teamId: string | null; blocked?: boolean } {
	const panelScope: LibraryScope = (input.panelScope || 'personal') as LibraryScope;
	const teamView: boolean = panelScope !== 'personal'
		|| input.selectedFilter === 'TEAM';
	if (!teamView) {
		return { scope: 'mine', teamId: null };
	}
	const activeTeamId: string = input.activeTeamId || '';
	if (!activeTeamId) {
		return { scope: 'all', teamId: null, blocked: true };
	}
	return { scope: 'all', teamId: activeTeamId };
}

export const LIBRARY_PARTIAL_FLAG = 'isPartialFromLibraryApi';

export function isPartialLibraryNodeRun (run: any): boolean {
	return !!(run && run[LIBRARY_PARTIAL_FLAG] === true);
}

/**
 * True when a `nodeRun.nodeDef` is a resolved object we can render from, rather
 * than a bare document reference string or a stub that never got its title.
 * The inverse is the "needs a Firestore fetch" condition, so callers on the
 * Firestore paths use `!isResolvedNodeDef(...)` instead of re-spelling the check.
 */
export function isResolvedNodeDef (nodeDef: any): boolean {
	return !!(nodeDef && typeof nodeDef !== 'string' && nodeDef.title);
}

export function mergeHydratedNodeRun (firestoreDoc: any, restPartial: any): any {
	if (!firestoreDoc) return restPartial;
	const merged = { ...firestoreDoc, ...restPartial };
	if (isResolvedNodeDef(firestoreDoc.nodeDef)) {
		merged.nodeDef = firestoreDoc.nodeDef;
	}
	delete merged[LIBRARY_PARTIAL_FLAG];
	return merged;
}


function present(value?: string | null): string | null {
	return value && value.trim() ? value.trim() : null;
}

/**
 * Map a `LibraryRequest` to the endpoint's snake_case query params. Pure — the
 * unit-tested core.
 *
 * This is a translation boundary: the filter panel's vocabulary in, the
 * endpoint's query string out. Three places where the two differ:
 *
 * - There is no `shape` parameter. The API derives the response shape from the
 *   calling client, and this browser is registered as `web_app`, so it is served
 *   `node_run` without asking. A public API caller gets the flat `result` shape
 *   and cannot request ours.
 * - The account is the `team_id` parameter: the panel's team picker becomes a
 *   comma-separated list, and no `team_id` at all is the personal account.
 *   `scope` only says whose rows within it — a team read is `all`.
 * - Favorites are two parameters because they answer two questions: `favorited`
 *   picks which rows, `sort` picks the order. The panel's Favorites tab wants
 *   most-recently-starred first; its favorite facet keeps the normal ordering.
 */
export function buildLibraryQueryParams(request: LibraryRequest): URLSearchParams {
	const params = new URLSearchParams();

/**
 * A date from the house picker to the matching instant for the API. The picker
 * may emit either `YYYY-MM-DD` or a full ISO timestamp for that selected day.
 *
 * Midnight in the BROWSER's timezone, not UTC. The user picked a day off their
 * own calendar, so "the 1st" has to mean their 1st: reading it as UTC midnight
 * shifts the window by the offset and quietly includes or drops the edge hours
 * for everyone outside UTC.
 *
 * `plusDays` exists for the exclusive end. Passing it through the Date
 * constructor rather than adding to the string is what makes month and year
 * rollover (and DST) the platform's problem instead of ours.
 */
const localMidnightIso = (day?: string | null, plusDays = 0): string | null => {
	if (!day) return null;
	const [year, month, date] = day.trim().slice(0, 10).split('-').map(Number);
	if (!year || !month || !date) return null;
	const at = new Date(year, month - 1, date + plusDays, 0, 0, 0, 0);
	return Number.isNaN(at.getTime()) ? null : at.toISOString();
};


	// The account selection. Named teams are a team read; the default `mine`
	// goes unsent for the personal listing, so the URL carries only what
	// deviates from the defaults.
	if (request.teamIds && request.teamIds.length) {
		params.set('team_id', request.teamIds.join(','));
	}
	if (request.scope === 'all') {
		params.set('scope', 'all');
	}

	if (request.limit) params.set('limit', String(request.limit));
	if (present(request.cursor)) params.set('cursor', request.cursor as string);

	const filters = request.filters || {};
	const setIf = (key: string, value?: string | null): void => {
		const v = present(value);
		if (v) params.set(key, v);
	};
	setIf('aspect_ratio', filters.aspectRatioFilter);
	setIf('resolution', filters.resolutionFilter);
	setIf('tool_id', filters.toolFilter);
	setIf('model_family_tool_tag_id', filters.modelFamilyTagFilter);
	setIf('media_tool_tag_id', filters.mediaTagFilter);
	// The panel's pickers are `type="date"`, so these are whole local days. v2's
	// window is half-open (`start_utc` inclusive, `end_utc` EXCLUSIVE), so the
	// end is the NEXT local midnight or the user silently loses the last day
	// they picked.
	setIf('start_utc', localMidnightIso(filters.dateFromFilter));
	setIf('end_utc', localMidnightIso(filters.dateToFilter, 1));
	// `user_id` filters within `scope=all` only; under `mine` the owner is
	// already the caller and the server answers 400 rather than overriding.
	if (request.scope === 'all') setIf('user_id', request.userId);

	// The Favorites tab wants most-recently-starred first; the favorite facet is
	// an overlay on the normal newest-created order. Both narrow to favorited
	// rows, so both set `favorited`; only the tab changes the sort.
	if (request.favorites) {
		params.set('favorited', 'true');
		params.set('sort', 'favorited');
		setIf('node_id', request.nodeId);
	} else if (present(filters.favoriteFilter)) {
		params.set('favorited', 'true');
	}

	// A search answers in one ranked page, so it never pages and never takes the
	// Favorites tab's sort. The server refuses `search` with either.
	const search = present(filters.searchFilter);
	if (search && search.length >= LIBRARY_SEARCH_MIN_LENGTH && !request.favorites && !present(request.cursor)) {
		params.set('search', search);
	}

	setIf('board_id', request.runnitId);
	return params;
}

function authedHeaders(
	idToken: string,
	deviceId?: string | null,
): Record<string, string> {
	const headers: Record<string, string> = {
		'Authorization': `Bearer ${idToken}`,
	};
	// Identifies this browser as the first-party web client, which is what
	// selects the node_run shape. Omitted only before registration completes.
	if (deviceId) headers['X-Device-Id'] = deviceId;
	return headers;
}

function objectRecord(value: unknown): Record<string, unknown> | null {
	return value !== null && typeof value === 'object' && !Array.isArray(value)
		? value as Record<string, unknown>
		: null;
}

/**
 * Turn v2 /me's Team Accounts into picker options. Membership determines
 * presence; the server-resolved permission snapshot determines whether an
 * option can be selected. The API remains the final authority when a Library
 * request is submitted.
 */
export function buildLibraryTeamOptions(accounts: unknown): LibraryTeamOption[] {
	if (!Array.isArray(accounts)) return [];

	const options: LibraryTeamOption[] = [];
	accounts.forEach((candidate: unknown) => {
		const account = objectRecord(candidate);
		if (!account || account.kind !== 'TEAM' || typeof account.id !== 'string' || !account.id) return;

		const permissions = objectRecord(account.permissions);
		options.push({
			value: account.id,
			text: typeof account.label === 'string' && account.label.trim()
				? account.label.trim()
				: account.id,
			disabled: permissions?.MANAGE_ALL_GENERATIONS !== true,
		});
	});

	return options.sort((a, b) => a.text.localeCompare(b.text));
}

/**
 * Whether the gallery's TEAM tab may be offered.
 *
 * This is a NARROWER question than the filter panel's Team scope. The panel
 * names its Teams explicitly, so it can answer for any eligible membership
 * (`teamOptions.some(o => !o.disabled)`). The TEAM tab names nothing: it reads
 * whichever Team the caller is currently acting as, so it is offered only when
 * that Team is itself eligible.
 *
 * Both halves matter. Under a Personal account the store keeps `team` bound to
 * the last Team (switching to Personal never clears it), so a TEAM tab offered
 * there reads a Team the caller never asked for. And under an ineligible active
 * Team the read is a guaranteed server denial even though another membership
 * would have been eligible.
 */
export function isActiveTeamLibraryEligible (input: {
	teamOptions?: LibraryTeamOption[] | null;
	activeTeamId?: string | null;
	actingAsTeam?: boolean;
}): boolean {
	if (!input.actingAsTeam) return false;
	const activeTeamId: string = input.activeTeamId || '';
	if (!activeTeamId) return false;
	return (input.teamOptions || []).some(
		(option: LibraryTeamOption) => option.value === activeTeamId && !option.disabled,
	);
}

/**
 * Read per-Team Library eligibility from v2 /me. This deliberately fails
 * closed to an empty option set: a transient identity read must never infer
 * permission from membership or from whichever Team is currently active.
 */
export async function fetchLibraryTeamOptions(input: LibraryTeamOptionsInput): Promise<LibraryTeamOption[]> {
	try {
		const resp = await fetch(`${input.baseUrl.replace(/\/$/, '')}/me`, {
			method: 'GET',
			headers: authedHeaders(input.idToken),
		});
		if (!resp.ok) return [];

		const body: unknown = await resp.json();
		const envelope = objectRecord(body);
		return buildLibraryTeamOptions(envelope?.accounts);
	} catch {
		return [];
	}
}

/**
 * Best-effort read of the API's error envelope: `{ error: { code, message } }`
 * (see restServer/api/errors.py error_payload). Never throws — a non-JSON or
 * empty error body just yields empty fields.
 */
async function readErrorEnvelope(resp: Response): Promise<{ code?: string; message?: string }> {
	try {
		const body: any = await resp.json();
		const err = (body && body.error) || {};
		return {
			code: typeof err.code === 'string' ? err.code : undefined,
			message: typeof err.message === 'string' ? err.message : undefined,
		};
	} catch {
		return {};
	}
}

export async function fetchLibraryPage(input: LibraryFetchInput): Promise<LibraryFetchResult> {
	const qs = buildLibraryQueryParams(input.request).toString();
	const url = `${input.baseUrl.replace(/\/$/, '')}/library?${qs}`;

	let resp: Response;
	try {
		resp = await fetch(url, {
			method: 'GET',
			headers: authedHeaders(input.idToken, input.deviceId),
		});
	} catch {
		return { ok: false, reason: 'network' };
	}

	if (!resp.ok) {
		// Read the server's error envelope instead of throwing it away. A 403 can
		// mean ACCOUNT_NOT_MEMBER, EMAIL_NOT_VERIFIED, ANONYMOUS_NOT_ALLOWED, or
		// the scope's PERMISSION_DENIED — all raised at different layers, and
		// collapsing them to "forbidden" hides which one fired.
		const { code, message } = await readErrorEnvelope(resp);

		// A rejected browser device cannot provide the expected web_app shape.
		// Forget it so the next attempt can register a fresh one rather than
		// replaying the unusable id until site data is cleared by hand.
		forgetWebClientDeviceIfRejected(code);

		const reason: 'unauthorized' | 'forbidden' | 'failed' = resp.status === 401
			? 'unauthorized'
			: (resp.status === 403 ? 'forbidden' : 'failed');
		return {
			ok: false,
			reason,
			...(code ? { code } : {}),
			message: message || `HTTP ${resp.status}`,
		};
	}

	let body: any;
	try {
		body = await resp.json();
	} catch {
		return { ok: false, reason: 'failed', message: 'Invalid JSON' };
	}

	return {
		ok: true,
		page: {
			data: Array.isArray(body.data) ? body.data : [],
			nodeDefs: body.tools || {},
			nextCursor: body.next_cursor ?? null,
			hasMore: !!body.has_more,
		},
	};
}
